import { Color3 } from '@babylonjs/core/Maths/math.color'
import { Effect } from '@babylonjs/core/Materials/effect'
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial'
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial'
import type { Scene } from '@babylonjs/core/scene'
import type { LocalProfile, ThemeId } from '../persistence/ProfileRepository'
import { COSMETICS, type ShellId, type TrailId } from '../content/CosmeticCatalog'

interface ThemePalette {
  readonly safe: Color3
  readonly secondary: Color3
  readonly atmosphere: Color3
}

const THEMES: Readonly<Record<ThemeId, ThemePalette>> = {
  ion: { safe: Color3.FromHexString('#42f5ff'), secondary: Color3.FromHexString('#407dff'), atmosphere: Color3.FromHexString('#071a32') },
  reactor: { safe: Color3.FromHexString('#ffc84a'), secondary: Color3.FromHexString('#ff7b27'), atmosphere: Color3.FromHexString('#251405') },
  nova: { safe: Color3.FromHexString('#dd55ff'), secondary: Color3.FromHexString('#7448ff'), atmosphere: Color3.FromHexString('#19092c') },
  ghost: { safe: Color3.FromHexString('#f2fbff'), secondary: Color3.FromHexString('#8cb8c9'), atmosphere: Color3.FromHexString('#101921') },
}

function registerShaders(): void {
  Effect.ShadersStore['neonTrackVertexShader'] = `
    precision highp float;
    attribute vec3 position;
    attribute vec3 normal;
    attribute vec2 uv;
    uniform mat4 world;
    uniform mat4 worldViewProjection;
    varying vec3 vWorldPosition;
    varying vec3 vNormal;
    varying vec2 vUV;
    void main(void) {
      vec4 worldPosition = world * vec4(position, 1.0);
      vWorldPosition = worldPosition.xyz;
      vNormal = normalize(mat3(world) * normal);
      vUV = uv;
      gl_Position = worldViewProjection * vec4(position, 1.0);
    }
  `
  Effect.ShadersStore['neonTrackFragmentShader'] = `
    precision highp float;
    varying vec3 vWorldPosition;
    varying vec3 vNormal;
    varying vec2 vUV;
    uniform vec3 uSafeColor;
    uniform float uTime;
    uniform float uSpeed;
    uniform float uContrast;
    void main(void) {
      float facing = 0.28 + 0.72 * max(0.0, vNormal.y);
      float gridX = smoothstep(0.94, 1.0, abs(sin(vWorldPosition.x * 1.35)));
      float gridZ = smoothstep(0.965, 1.0, abs(sin(vWorldPosition.z * 0.43)));
      float grid = max(gridX * 0.18, gridZ * 0.25);
      float flow = fract(vWorldPosition.z * 0.055 - uTime * (0.38 + uSpeed * 0.7));
      float pulse = smoothstep(0.89, 0.99, flow) * (0.25 + 0.45 * uSpeed);
      float distanceFade = 1.0 - smoothstep(120.0, 440.0, abs(vWorldPosition.z));
      vec3 graphite = vec3(0.045, 0.068, 0.105) * (0.7 + facing * 0.3) * uContrast;
      vec3 energy = uSafeColor * (grid * distanceFade * 1.35 + pulse * 0.16);
      gl_FragColor = vec4(graphite + energy, 1.0);
    }
  `
  Effect.ShadersStore['neonHeroVertexShader'] = Effect.ShadersStore['neonTrackVertexShader']!
  Effect.ShadersStore['neonHeroFragmentShader'] = `
    precision highp float;
    varying vec3 vWorldPosition;
    varying vec3 vNormal;
    varying vec2 vUV;
    uniform vec3 uSafeColor;
    uniform vec3 cameraPosition;
    uniform float uTime;
    uniform float uSpeed;
    uniform float uSkin;
    void main(void) {
      vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
      float fresnel = pow(1.0 - max(0.0, dot(normalize(vNormal), viewDirection)), 2.7);
      float angular = atan(vWorldPosition.z, vWorldPosition.x);
      float bands = vWorldPosition.y * (8.0 + mod(uSkin, 4.0) * 1.7) + angular * (2.0 + mod(uSkin, 3.0));
      float channels = smoothstep(0.91, 0.99, abs(sin(bands - uTime * (1.7 + uSkin * 0.04))));
      vec3 shell = vec3(0.055, 0.078, 0.115) + vec3(0.22) * pow(max(0.0, dot(vNormal, normalize(vec3(-0.4, 0.8, -0.2)))), 12.0);
      vec3 energy = uSafeColor * (channels * (0.24 + uSpeed * 0.45) + fresnel * (0.34 + uSpeed * 0.65));
      gl_FragColor = vec4(shell + energy, 1.0);
    }
  `
  Effect.ShadersStore['neonObstacleVertexShader'] = Effect.ShadersStore['neonTrackVertexShader']!
  Effect.ShadersStore['neonObstacleFragmentShader'] = `
    precision highp float;
    varying vec3 vWorldPosition;
    varying vec3 vNormal;
    varying vec2 vUV;
    uniform float uTime;
    void main(void) {
      float stripe = step(0.5, fract((vWorldPosition.x + vWorldPosition.y) * 0.8 - uTime * 0.7));
      float rim = pow(1.0 - abs(vNormal.z), 2.0);
      vec3 body = vec3(0.055, 0.012, 0.018) + vec3(0.08) * max(0.0, vNormal.y);
      vec3 hazard = mix(vec3(0.46, 0.015, 0.025), vec3(1.0, 0.08, 0.015), stripe) * (0.45 + rim * 0.75);
      gl_FragColor = vec4(body + hazard, 1.0);
    }
  `
}

export class MaterialLibrary {
  public readonly track: ShaderMaterial
  public readonly hero: ShaderMaterial
  public readonly obstacle: ShaderMaterial
  public readonly edge: StandardMaterial
  public readonly dark: StandardMaterial
  public readonly hologram: StandardMaterial
  public readonly trail: StandardMaterial
  public readonly debug: StandardMaterial
  private palette = THEMES.ion

  public constructor(scene: Scene) {
    registerShaders()
    this.track = new ShaderMaterial('track-surface', scene, 'neonTrack', {
      attributes: ['position', 'normal', 'uv'],
      uniforms: ['world', 'worldViewProjection', 'uSafeColor', 'uTime', 'uSpeed', 'uContrast'],
    })
    this.hero = new ShaderMaterial('hero-ball', scene, 'neonHero', {
      attributes: ['position', 'normal', 'uv'],
      uniforms: ['world', 'worldViewProjection', 'cameraPosition', 'uSafeColor', 'uTime', 'uSpeed', 'uSkin'],
    })
    this.obstacle = new ShaderMaterial('hazard', scene, 'neonObstacle', {
      attributes: ['position', 'normal', 'uv'],
      uniforms: ['world', 'worldViewProjection', 'uTime'],
    })
    this.edge = new StandardMaterial('edge-energy', scene)
    this.edge.disableLighting = true
    this.dark = new StandardMaterial('structure-dark', scene)
    this.dark.diffuseColor = Color3.FromHexString('#122235')
    this.dark.specularColor = Color3.FromHexString('#37586b')
    this.dark.roughness = 0.82
    this.hologram = new StandardMaterial('hologram', scene)
    this.hologram.disableLighting = true
    this.hologram.alpha = 0.34
    this.hologram.backFaceCulling = false
    this.trail = new StandardMaterial('hero-trail-energy', scene)
    this.trail.disableLighting = true
    this.trail.alpha = 0.46
    this.trail.backFaceCulling = false
    this.debug = new StandardMaterial('collider-debug', scene)
    this.debug.disableLighting = true
    this.debug.wireframe = true
    this.debug.emissiveColor = Color3.FromHexString('#ff3bd4')
    this.debug.alpha = 0.9
    this.applyTheme('ion', false)
    this.hero.setFloat('uSkin', 0)
  }

  public update(timeSeconds: number, speedNormalized: number, highContrast: boolean): void {
    this.track.setFloat('uTime', timeSeconds)
    this.track.setFloat('uSpeed', speedNormalized)
    this.track.setFloat('uContrast', highContrast ? 1.75 : 1)
    this.hero.setFloat('uTime', timeSeconds)
    this.hero.setFloat('uSpeed', speedNormalized)
    this.obstacle.setFloat('uTime', timeSeconds)
  }

  public applyProfile(profile: LocalProfile): void {
    this.applyTheme(profile.theme, profile.highContrast)
    this.setHeroSkin(profile.shell)
    this.setTrailSkin(profile.trail)
  }

  public setHeroSkin(shell: ShellId): void {
    this.hero.setFloat('uSkin', Math.max(0, COSMETICS.shells.findIndex((entry) => entry.id === shell)))
  }

  public setTrailSkin(trail: TrailId): void {
    const colors: Readonly<Record<TrailId, string>> = {
      ion: '#42f5ff',
      pulse: '#4f8dff',
      ember: '#ff7438',
      prism: '#e45bff',
      ghost: '#f2fbff',
      vector: '#56ff9a',
      rift: '#8c5cff',
      apex: '#ffd75b',
    }
    const color = Color3.FromHexString(colors[trail])
    this.trail.emissiveColor = color
    this.trail.diffuseColor = color.scale(0.06)
  }

  public get atmosphereColor(): Color3 {
    return this.palette.atmosphere
  }

  public dispose(): void {
    this.track.dispose()
    this.hero.dispose()
    this.obstacle.dispose()
    this.edge.dispose()
    this.dark.dispose()
    this.hologram.dispose()
    this.trail.dispose()
    this.debug.dispose()
  }

  private applyTheme(theme: ThemeId, highContrast: boolean): void {
    this.palette = THEMES[theme]
    const safe = highContrast ? Color3.Lerp(this.palette.safe, Color3.White(), 0.28) : this.palette.safe
    this.track.setColor3('uSafeColor', safe)
    this.hero.setColor3('uSafeColor', safe)
    this.edge.emissiveColor = safe.scale(highContrast ? 1.5 : 1)
    this.edge.diffuseColor = safe.scale(0.08)
    this.hologram.emissiveColor = this.palette.secondary
  }
}
